import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import ValueMetricCard from './components/ValueMetricCard';
import UsageTrendsChart from './components/UsageTrendsChart';
import CostSavingsCalculator from './components/CostSavingsCalculator';
import ROITimelineChart from './components/ROITimelineChart';
import { supabase } from '../../lib/supabase';

const ClientValueAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [valueMetrics, setValueMetrics] = useState({
    totalPayrollProcessed: 0,
    employeesManaged: 0,
    costSavings: 0,
    roiPercentage: 0,
    roiTrend: 'up'
  });
  const [usageData, setUsageData] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('12months');

  useEffect(() => {
    fetchValueMetrics();
    fetchUsageData();
  }, [selectedPeriod]);

  const fetchValueMetrics = async () => {
    try {
      setLoading(true);

      // Fetch payroll data
      const { data: payrollData, error: payrollError } = await supabase?.from('payroll_runs')?.select('total_gross, total_net')?.eq('payroll_status', 'paid');

      if (payrollError) throw payrollError;

      // Fetch employee count
      const { data: employeeData, error: employeeError } = await supabase?.from('employees')?.select('id')?.eq('is_active', true);

      if (employeeError) throw employeeError;

      // Fetch subscription data
      const { data: subscriptionData, error: subscriptionError } = await supabase?.from('company_subscriptions')?.select('monthly_price_kina, active_employee_count')?.single();

      if (subscriptionError) throw subscriptionError;

      // Calculate metrics
      const totalPayroll = payrollData?.reduce((sum, run) => sum + parseFloat(run?.total_gross || 0), 0) || 0;
      const employeeCount = employeeData?.length || 0;
      
      // Cost savings calculation: Manual processing cost vs subscription cost
      // Assume manual HR processing costs K50 per employee per month
      const manualProcessingCost = employeeCount * 50 * 12; // Annual
      const subscriptionCost = parseFloat(subscriptionData?.monthly_price_kina || 0) * 12;
      const savings = manualProcessingCost - subscriptionCost;
      const roi = subscriptionCost > 0 ? ((savings / subscriptionCost) * 100) : 0;

      setValueMetrics({
        totalPayrollProcessed: totalPayroll,
        employeesManaged: employeeCount,
        costSavings: savings,
        roiPercentage: roi,
        roiTrend: roi > 0 ? 'up' : 'down'
      });

    } catch (error) {
      console.error('Error fetching value metrics:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageData = async () => {
    try {
      // Fetch monthly usage trends
      const { data, error } = await supabase?.from('payroll_runs')?.select('pay_period_start, pay_period_end, total_gross, payroll_status')?.order('pay_period_start', { ascending: true })?.limit(12);

      if (error) throw error;

      setUsageData(data || []);
    } catch (error) {
      console.error('Error fetching usage data:', error?.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PG', {
      style: 'currency',
      currency: 'PGK',
      minimumFractionDigits: 2
    })?.format(amount || 0);
  };

  return (
    <>
      <Helmet>
        <title>Client Value Analytics Dashboard - PNG HR Analytics</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Client Value Analytics Dashboard</h1>
              <p className="mt-2 text-gray-600">ROI visualization and subscription value justification</p>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e?.target?.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="3months">Last 3 Months</option>
                <option value="6months">Last 6 Months</option>
                <option value="12months">Last 12 Months</option>
                <option value="all">All Time</option>
              </select>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Icon name="Download" className="w-5 h-5" />
                Export Report
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Executive Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <ValueMetricCard
                title="Total Payroll Processed"
                value={formatCurrency(valueMetrics?.totalPayrollProcessed)}
                icon="DollarSign"
                trend="up"
                trendValue="+12%"
                color="blue"
              />
              <ValueMetricCard
                title="Employees Managed"
                value={valueMetrics?.employeesManaged?.toString()}
                icon="Users"
                trend="up"
                trendValue="+5"
                color="green"
              />
              <ValueMetricCard
                title="Cost Savings"
                value={formatCurrency(valueMetrics?.costSavings)}
                icon="TrendingUp"
                trend={valueMetrics?.roiTrend}
                trendValue={`${valueMetrics?.roiPercentage?.toFixed(1)}%`}
                color="purple"
              />
              <ValueMetricCard
                title="Subscription ROI"
                value={`${valueMetrics?.roiPercentage?.toFixed(1)}%`}
                icon="Target"
                trend={valueMetrics?.roiTrend}
                trendValue="vs manual"
                color="orange"
              />
            </div>

            {/* Main Analytics Panels */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Usage Trends - 2 columns */}
              <div className="lg:col-span-2">
                <UsageTrendsChart data={usageData} />
              </div>

              {/* Cost Savings Calculator - 1 column */}
              <div className="lg:col-span-1">
                <CostSavingsCalculator
                  employeeCount={valueMetrics?.employeesManaged}
                  costSavings={valueMetrics?.costSavings}
                />
              </div>
            </div>

            {/* ROI Timeline */}
            <div className="mb-8">
              <ROITimelineChart
                totalPayroll={valueMetrics?.totalPayrollProcessed}
                costSavings={valueMetrics?.costSavings}
                roiPercentage={valueMetrics?.roiPercentage}
              />
            </div>

            {/* Value Proposition Summary */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription Value Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Automated Processing</h3>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(valueMetrics?.totalPayrollProcessed)}</p>
                  <p className="text-sm text-gray-500 mt-1">Total payroll processed automatically</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Compliance Automation</h3>
                  <p className="text-2xl font-bold text-gray-900">100%</p>
                  <p className="text-sm text-gray-500 mt-1">PNG labor law compliance rate</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Time Savings</h3>
                  <p className="text-2xl font-bold text-gray-900">85%</p>
                  <p className="text-sm text-gray-500 mt-1">Reduction in manual HR tasks</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ClientValueAnalyticsDashboard;